import { Box, TableCell, TableRow, Typography, Card, Grid, Menu, ListItemIcon, ListItemText } from "@material-ui/core";
import { makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardArrowRight, KeyboardArrowDown, InsertDriveFileOutlined, DeleteOutline, MoreVert, CloudCircle, Delete, IndeterminateCheckBoxOutlined, AddBoxOutlined, Layers } from "@material-ui/icons";
import { DateFormat, ELMSURE, formatDate, isNil } from "@roc/feature-utils";
import { IconButton, MenuItem } from "@roc/ui";
import Chip from '@material-ui/core/Chip';
import { formatCurrency, formatPercentage } from "@roc/ui/utils";
import { observer } from "mobx-react";
import React, { Fragment, useEffect, useState } from "react";
import EditPricingEconomicTierDialog from "./editPricingEconomicTierDialog";

const useStyles = makeStyles((theme: Theme) => ({
  noBorderCell: {
    border: 0,
  },
  borderCell: {
    border: '1px solid rgba(224, 224, 224, 1)',
    backgroundColor: '#f8f8f8',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  documentCard: {
    borderRadius: 0,
    padding: '8px',
    display: 'flex',
    backgroundColor: '#F5F5F5',
    height: '100%'
  },
  documentCardContainer: {
    padding: '8px',
  },
  documentName: {
    padding: '8px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center'
  },
  headerRow: {
    border: '1px solid #ccc',
    '& td': {
      padding: '8px'
    },
  },
  headerRowWitBg: {
    backgroundColor: '#f8f8f8',
    border: '1px solid #ccc',
    '& td': {
      padding: '8px'
    },
  },
  documentIconContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  cardIcon: {
    cursor: 'pointer'
  },
  boldValues: {
    fontWeight: 'bold'
  },
  greenChip: {
    color: 'rgb(109, 153, 66)',
    borderColor: '#89C053',
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: 'white'
  },
  grayChip: {
    color: 'rgba(0, 0, 0, 0.54)',
    borderColor: 'rgba(0, 0, 0, 0.54)',
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: 'white'
  }
}));

export const TierPricingEconomicsTableRow = observer(({ termTier, index, expandAll = undefined, allowActions = false, readOnly = true, openConfirmationDialog, openEditPricingEconomicTierDialog, openEditBorrowersFromTierDialog, openEditBorrowerTierDialog, lenderTermTierEconomicsFormStore }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const classes = useStyles({});

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!isNil(expandAll)) {
      setOpen(expandAll);
    }
  }, [expandAll])

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const deleteBorrower = (borrowerId) => {
    lenderTermTierEconomicsFormStore.setTermTierToDelete(termTier.submissionPricingTiers.tierId)
    lenderTermTierEconomicsFormStore.setBorrowerToDelete(borrowerId)
    lenderTermTierEconomicsFormStore.deleteBorrowerFromTier()
  };

  return (
    <Fragment key={termTier?.submissionPricingTiers.tierId}>
      <TableRow className={classes.headerRow}>
        <TableCell>
          <Box
            display={'flex'}
            alignItems={'center'}
            px={1}
            py={1}
            ml={0}
            onClick={() => {
              setOpen(!open);
            }}
            style={{
              cursor: 'pointer',
            }}
          >
            <Box component={'span'}>
              <IconButton
                testId={`expand-${termTier?.submissionPricingTiers.tierId}`}
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <IndeterminateCheckBoxOutlined /> : <AddBoxOutlined />}
              </IconButton>
            </Box>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>{termTier.submissionPricingTiers.name}</Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}><Chip className={termTier.submissionPricingTiers.isDefaultTier ? classes.greenChip : classes.grayChip} label={termTier.submissionPricingTiers.isDefaultTier ?
              'Yes' : 'No'
            } /></Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}><Chip className={termTier.submissionPricingTiers.disablePricing ? classes.greenChip : classes.grayChip} label={termTier.submissionPricingTiers.disablePricing ? 'Yes' : 'No'} /></Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>{formatPercentage(termTier.submissionPricingTiers.yieldSpreadPremium) ?? '-'}</Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {termTier.submissionPricingTiers.termOriginationPoints ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {formatCurrency(termTier.submissionPricingTiers.processingFees, 0) ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {formatCurrency(termTier.submissionPricingTiers.adminFees, 0) ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {formatCurrency(termTier.submissionPricingTiers.underwriterFees, 0) ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {formatCurrency(termTier.submissionPricingTiers.commitmentFees, 0) ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <Typography className={classes.boldValues}>
              {formatCurrency(termTier.submissionPricingTiers.buydownFees, 0) ?? '-'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell >
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent="space-between"
            px={0}
            py={1}
            ml={0}
          >
            <IconButton
              testId={'manage-borrowers'}
              onClick={e => openMenu(e)}>
              <MoreVert color="primary" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {open && termTier.lenderBorrowers != null &&
        <>
          {termTier.lenderBorrowers.length == 0 ?
            <TableRow>
              <TableCell colSpan={12} className={classes.borderCell}>
                <Box textAlign={'center'}>
                  {"- Empty -"}
                </Box>
              </TableCell>
            </TableRow>
            :
            <>
              {termTier.lenderBorrowers.map((borrower, index) => (
                <TableRow className={classes.headerRowWitBg}>
                  <TableCell className={classes.noBorderCell} />
                  <TableCell style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }} className={classes.noBorderCell} colSpan={3}>
                    <Box
                      sx={{
                        width: '150px',
                      }}
                    >
                      <Typography className={classes.label}>
                        {borrower.firstName + " " + borrower.lastName}
                      </Typography>
                    </Box>
                    <IconButton
                      testId={`delete-doc`}
                      onClick={() => openEditBorrowerTierDialog(borrower.borrowerId, termTier.submissionPricingTiers.tierId)}
                      style={{ padding: 0, marginLeft: 16, width: '0.8em' }}
                    >
                      <Layers color="primary" />
                    </IconButton>
                    <IconButton
                      testId={`delete-doc2`}
                      onClick={() => deleteBorrower(borrower.borrowerId)}
                      style={{ padding: 0, marginLeft: 16, width: '0.8em' }}
                    >
                      <Delete color="primary" />
                    </IconButton>
                  </TableCell>
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                  <TableCell className={classes.noBorderCell} />
                </TableRow>
              ))}
            </>
          }
        </>
      }
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left', }}
        transformOrigin={{ vertical: 'top', horizontal: 'left', }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {true && (
          <>
            <MenuItem
              testId={`download-reports`}
              onClick={() => openEditBorrowersFromTierDialog(termTier.submissionPricingTiers.tierId)}
            >
              <ListItemText>Add / Remove Borrower</ListItemText>
            </MenuItem>
            <MenuItem
              testId={`download-reports`}
              onClick={() => openEditPricingEconomicTierDialog(termTier.submissionPricingTiers.tierId)}
            >
              <ListItemText>Edit Tier Economics</ListItemText>
            </MenuItem>
            <MenuItem
              testId={`download-reports`}
              style={{ borderTop: 'solid 1px #ccc' }}
              onClick={() => openConfirmationDialog(termTier.submissionPricingTiers.tierId)}
            >
              <ListItemText>Delete Tier</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Fragment>
  )
});